import { Controller, useFormContext } from 'react-hook-form';
import { CustomInput, Input, UISelect } from '../common/forms';
import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { errorMessage, successMessage } from '~/utils/toasts';

import { ArrivalAndDepartureDetails } from './arrival-and-departure-details';
import InputMask from 'react-input-mask';
import { RequestFooter } from './request-footer';
import { submitSupervisorAndArrivalDetails } from '~/apis/dhamen';
import { useRouter } from 'next/router';
import Axios from '~/utils/axios';
import { sentryCaptureException } from '~/errors/sentry';
import { renderErrorsToToaster } from './render-error-to-toaster';
import PhonePicker from '../common/forms/phone-picker';
import {
   validateSupervisorFlightDetails,
   ValidateDataProps,
} from '~/apis/dhamen/validate-supervisor-flight-details';

export const SupervisorDetails = ({
   embassiesList,
   countriesList,
   airCarrierList,
   seaCarriersList,
   landCarriersList,
   airPortsList,
   seaPortsList,
   landPortsList,
}: any) => {
   const { translate, lang } = useTranslate();
   const [loading, setLoading] = useState(false);

   const { query } = useRouter();
   const router = useRouter();
   const isEdit = Boolean(query.id);

   const {
      register,
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      getValues,
      reset,
      watch,
   } = useFormContext<any>();

   const submitForm = async (values: any) => {
      if (loading) return;
      setLoading(true);
      const body = {
         ...values,
         current_step: 'main_information_supervisor',
         flightPrice: parseFloat(values?.flightPrice || 0)?.toFixed(2),
      };

      if (isEdit) {
         try {
            const { data } = await Axios.patch(`/dhamen/v1/visa-request/${query.id}`, body);
            if (data?.status.toLowerCase() === 'success') {
               reset({ ...getValues(), ...values });
               successMessage(translate({ id: 'visasManager:submit message' }));
               router.push(`/${lang}/umrah-requests/${query.id}/edit/accommodation-details`);
            } else {
               throw new Error(data?.error || data?.original?.error || 'Something went wrong');
            }
         } catch (error: any) {
            console.log(error);
            sentryCaptureException(error);
            renderErrorsToToaster(error?.response?.data);
            errorMessage(translate({ id: 'common:something went wrong' }));
            setLoading(false);
         }
      } else {
         const objForValidation: ValidateDataProps = {
            embassyCode: body.visaIssueEmbassy,
            nationalityCode: body.issueCountry,
            countryCode: body.country_code?.startsWith?.('+')
               ? body.country_code?.slice?.(1)
               : body.country_code,
            flightInfo: {
               arrivalFlightNumber: body.entryFlightNo,
               arrivalFlightDate: body.dateArrivalFlight,
               arrivalFlightTime: body.arrivalFlightTime,
               arrivalCarrierCode: body.carrierIn,
            },
         };
         // if (user?.tourism_number) objForValidation.dmcId = user?.tourism_number;

         // Data Validation API Call
         const { data } =
            body.entryType === 'AIR'
               ? await validateSupervisorFlightDetails(objForValidation)
               : { data: [] };
         if (data.length) {
            setLoading(false);
            data?.forEach?.((error: { statusMessage: string }) => {
               errorMessage(error?.statusMessage);
            });
         } else {
            const { data } = await submitSupervisorAndArrivalDetails(body);
            if (data?._id) {
               successMessage(translate({ id: 'visasManager:submit message' }));
               router.push(`/${lang}/umrah-requests/${data._id}/edit/accommodation-details`);
            } else {
               // errorMessage(translate({ id: 'common:some error' }));
               setLoading(false);
            }
         }
      }
   };

   const EntryTypeOptions = [
      {
         value: 'LAND',
         label: translate({ id: 'visasManager:land' }),
      },
      {
         value: 'SEA',
         label: translate({ id: 'visasManager:sea' }),
      },
      {
         value: 'AIR',
         label: translate({ id: 'visasManager:air' }),
      },
   ];

   const selectedCountry = watch('issueCountry');
   const filteredEmbassiesListList = embassiesList.filter(
      (hotel: any) => hotel.countryCode === selectedCountry
   );
   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:supervisor details" />
            </div>

            <div className="w-full">
               <form onSubmit={handleSubmit(submitForm)}>
                  <div className="mb-5">
                     <Input
                        id="name"
                        label={<Translate id="visasManager:umrah package name" />}
                        placeholder={translate({ id: 'visasManager:enter name of your package' })}
                        isRequired
                        error={errors.name?.message}
                        {...register('name', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^[A-Za-z\s\u0600-\u06FF]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="grid w-full grid-cols-1 gap-5 mb-5 lg:grid-cols-2">
                     <Controller
                        name="issueCountry"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select country',
                              })}
                              label={translate({
                                 id: 'visasManager:issue country',
                              })}
                              id="issueCountry"
                              value={countriesList.find((opt: any) => opt.value === value)}
                              options={countriesList}
                              error={errors.issueCountry?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                           />
                        )}
                     />
                     <Controller
                        name="visaIssueEmbassy"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select visa issue embassy',
                              })}
                              label={translate({
                                 id: 'visasManager:visa issue embassy',
                              })}
                              id="visaIssueEmbassy"
                              value={filteredEmbassiesListList.find(
                                 (opt: any) => opt.value === value
                              )}
                              defaultValue={getValues('visaIssueEmbassy')}
                              options={filteredEmbassiesListList}
                              error={errors.visaIssueEmbassy?.message}
                              onChange={({ value }: any) => onChange(value)}
                              isRequired
                              isDisabled={Boolean(!selectedCountry?.length)}
                           />
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        id="superVisorName"
                        label={<Translate id="visasManager:supervisor name" />}
                        placeholder={translate({ id: 'visasManager:enter supervisor name' })}
                        isRequired
                        error={errors.superVisorName?.message}
                        {...register('superVisorName', {
                           required: translate({ id: 'validation:required' }),
                           pattern: {
                              value: /^[A-Za-z\s\u0600-\u06FF]+$/,
                              message: translate({ id: 'validation:letters only' }),
                           },
                           minLength: {
                              value: 3,
                              message: translate({
                                 id: 'validation:min characters',
                                 values: {
                                    count: String(3),
                                 },
                              }),
                           },
                           maxLength: {
                              value: 60,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(60),
                                 },
                              }),
                           },
                        })}
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="supervisorIdNumber"
                        rules={{
                           validate: value => value > 0 || translate({ id: 'validation:required' }),
                           minLength: {
                              value: 3,
                              message: translate({
                                 id: 'validation:min characters',
                                 values: {
                                    count: String(3),
                                 },
                              }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="9999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="supervisorIdNumber"
                                    label={<Translate id="visasManager:supervisor id number" />}
                                    placeholder={translate({
                                       id: 'visasManager:enter supervisor id number',
                                    })}
                                    isRequired
                                    error={errors.supervisorIdNumber?.message}
                                    ref={ref}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  </div>
                  <div className="mb-5">
                     <Input
                        label={<Translate id="visasManager:supervisor mobile number" />}
                        labelClassName="!text-base font-bold"
                        placeholder=""
                        error={errors.phone_number?.message}
                        {...register('phone_number', {
                           validate: value => value > 0 || translate({ id: 'validation:required' }),
                           minLength: {
                              // Subtracting from 10 as country_code includes +
                              value: 10 - getValues('country_code')?.length || 9,
                              message: translate({
                                 id: 'validation:min characters',
                                 values: {
                                    count: String(
                                       String(10 - getValues('country_code')?.length || 9)
                                    ),
                                 },
                              }),
                           },
                           maxLength: {
                              value: 16 - getValues('country_code')?.length || 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(16 - getValues('country_code')?.length || 15),
                                 },
                              }),
                           },
                        })}
                        type="number"
                        inputMode="numeric"
                        isRequired
                        inputWrapperClassName="relative"
                        prepend={
                           <div className="flex items-center justify-center w-[120px] h-full -ml-2 -mr-6 bg-gray-100 ltr:rounded-l-md rtl:rounded-r-md">
                              <PhonePicker
                                 placeholder="XXX"
                                 onCountrySelected={({ phone }) =>
                                    setValue('country_code', !phone ? '' : `+${phone}`, {
                                       shouldValidate: true,
                                    })
                                 }
                                 value={getValues('country_code') || null}
                              />
                           </div>
                        }
                     />
                  </div>

                  <div className="mb-5">
                     <Controller
                        name="entryType"
                        control={control}
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <UISelect
                              placeholder={translate({
                                 id: 'visasManager:select entry type',
                              })}
                              label={translate({
                                 id: 'visasManager:entry type',
                              })}
                              id="entryType"
                              value={EntryTypeOptions.find(opt => opt.value === value)}
                              options={EntryTypeOptions}
                              error={errors.entryType?.message}
                              onChange={({ value }: any) => {
                                 onChange(value);
                                 setValue('carrierOut', '');
                                 setValue('carrierIn', '');
                                 setValue('entryPort', '');
                                 setValue('exitPort', '');
                              }}
                              isRequired
                           />
                        )}
                     />
                  </div>
                  <div className="mt-8">
                     <ArrivalAndDepartureDetails
                        airCarrierList={airCarrierList}
                        landCarriersList={landCarriersList}
                        seaCarriersList={seaCarriersList}
                        airPortsList={airPortsList}
                        seaPortsList={seaPortsList}
                        landPortsList={landPortsList}
                     />
                  </div>
               </form>
            </div>
         </div>

         <RequestFooter fwdBtnHandler={handleSubmit(submitForm)} loading={loading} />
      </>
   );
};
